import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { ArrowLeftIcon, UserIcon, MailIcon, PhoneIcon, BuildingIcon, MapPinIcon, SaveIcon, CheckCircleIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BillingAddress } from '../types';
export function CustomerProfile() {
  const navigate = useNavigate();
  const {
    user,
    updateUser
  } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    company: user?.company || '',
    billingAddress: user?.billingAddress || {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States'
    }
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  if (!user) return null;
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.billingAddress.street.trim()) newErrors.street = 'Street address is required';
    if (!formData.billingAddress.city.trim()) newErrors.city = 'City is required';
    if (!formData.billingAddress.state.trim()) newErrors.state = 'State is required';
    if (!formData.billingAddress.zipCode.trim()) newErrors.zipCode = 'ZIP code is required';
    if (!formData.billingAddress.country.trim()) newErrors.country = 'Country is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = () => {
    if (!validateForm()) return;
    if (updateUser) {
      updateUser({
        name: formData.name,
        phone: formData.phone,
        company: formData.company,
        billingAddress: formData.billingAddress
      });
    }
    setIsEditing(false);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };
  const handleCancel = () => {
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      company: user.company || '',
      billingAddress: user.billingAddress || {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'United States'
      }
    });
    setErrors({});
    setIsEditing(false);
  };
  return <div className="min-h-screen bg-slate-50" data-id="element-465">
      {/* Success Toast */}
      <AnimatePresence data-id="element-466">
        {showSuccess && <motion.div initial={{
        opacity: 0,
        y: -50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -50
      }} className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-green-500 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-3" data-id="element-467">
            <CheckCircleIcon className="w-5 h-5" data-id="element-468" />
            <span className="font-semibold" data-id="element-469">Profile updated successfully</span>
          </motion.div>}
      </AnimatePresence>

      {/* Header */}
      <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6" data-id="element-470">
        <div className="flex items-center gap-4 mb-6" data-id="element-471">
          <motion.button whileTap={{
          scale: 0.9
        }} onClick={() => navigate('/')} className="p-2 hover:bg-slate-700 rounded-lg transition-colors" data-id="element-472">
            <ArrowLeftIcon className="w-6 h-6" data-id="element-473" />
          </motion.button>
          <div data-id="element-474">
            <h1 className="text-2xl font-bold" data-id="element-475">Profile</h1>
            <p className="text-slate-300 text-sm" data-id="element-476">Manage your account</p>
          </div>
        </div>

        <div className="flex items-center gap-4" data-id="element-477">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 15
        }} className="w-20 h-20 bg-orange-600 rounded-2xl flex items-center justify-center" data-id="element-478">
            <UserIcon className="w-10 h-10 text-white" data-id="element-479" />
          </motion.div>
          <div data-id="element-480">
            <h2 className="text-xl font-bold" data-id="element-481">{user.name}</h2>
            <p className="text-slate-300" data-id="element-482">{user.email}</p>
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="p-6 space-y-6" data-id="element-483">
        {/* Account Information */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white rounded-xl shadow-md p-6 space-y-4" data-id="element-484">
          <div className="flex items-center justify-between mb-4" data-id="element-485">
            <h3 className="font-bold text-slate-900" data-id="element-486">Account Information</h3>
            {!isEditing && <motion.div whileTap={{
            scale: 0.95
          }} data-id="element-487">
                <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)} data-id="element-488">
                  Edit
                </Button>
              </motion.div>}
          </div>

          <Input label="Full Name" value={formData.name} onChange={e => setFormData({
          ...formData,
          name: e.target.value
        })} icon={<UserIcon className="w-5 h-5" data-id="element-490" />} disabled={!isEditing} error={errors.name} required data-id="element-489" />

          <Input label="Email" value={formData.email} icon={<MailIcon className="w-5 h-5" data-id="element-492" />} disabled data-id="element-491" />

          <Input label="Phone" value={formData.phone} onChange={e => setFormData({
          ...formData,
          phone: e.target.value
        })} icon={<PhoneIcon className="w-5 h-5" data-id="element-494" />} disabled={!isEditing} data-id="element-493" />

          <Input label="Company" value={formData.company} onChange={e => setFormData({
          ...formData,
          company: e.target.value
        })} icon={<BuildingIcon className="w-5 h-5" data-id="element-496" />} disabled={!isEditing} data-id="element-495" />
        </motion.div>

        {/* Billing Address */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="bg-white rounded-xl shadow-md p-6 space-y-4" data-id="element-497">
          <div className="flex items-center gap-2 mb-4" data-id="element-498">
            <MapPinIcon className="w-5 h-5 text-orange-600" data-id="element-499" />
            <h3 className="font-bold text-slate-900" data-id="element-500">Billing Address</h3>
            <span className="text-xs font-semibold text-red-600 ml-1" data-id="element-501">
              *Required
            </span>
          </div>

          <Input label="Street Address" placeholder="123 Main Street" value={formData.billingAddress.street} onChange={e => setFormData({
          ...formData,
          billingAddress: {
            ...formData.billingAddress,
            street: e.target.value
          }
        })} disabled={!isEditing} error={errors.street} required data-id="element-502" />

          <div className="grid grid-cols-2 gap-4" data-id="element-503">
            <Input label="City" placeholder="Austin" value={formData.billingAddress.city} onChange={e => setFormData({
            ...formData,
            billingAddress: {
              ...formData.billingAddress,
              city: e.target.value
            }
          })} disabled={!isEditing} error={errors.city} required data-id="element-504" />

            <Input label="State" placeholder="TX" value={formData.billingAddress.state} onChange={e => setFormData({
            ...formData,
            billingAddress: {
              ...formData.billingAddress,
              state: e.target.value
            }
          })} disabled={!isEditing} error={errors.state} required data-id="element-505" />
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-506">
            <Input label="ZIP Code" placeholder="78701" value={formData.billingAddress.zipCode} onChange={e => setFormData({
            ...formData,
            billingAddress: {
              ...formData.billingAddress,
              zipCode: e.target.value
            }
          })} disabled={!isEditing} error={errors.zipCode} required data-id="element-507" />

            <Input label="Country" placeholder="United States" value={formData.billingAddress.country} onChange={e => setFormData({
            ...formData,
            billingAddress: {
              ...formData.billingAddress,
              country: e.target.value
            }
          })} disabled={!isEditing} error={errors.country} required data-id="element-508" />
          </div>
        </motion.div>

        {/* Notifications */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="bg-white rounded-xl shadow-md p-6" data-id="element-509">
          <h3 className="font-bold text-slate-900 mb-4" data-id="element-510">Notifications</h3>
          <div className="space-y-3" data-id="element-511">
            <label className="flex items-center justify-between" data-id="element-512">
              <span className="text-slate-700" data-id="element-513">Job status updates</span>
              <input type="checkbox" defaultChecked className="w-5 h-5 text-orange-600 rounded" data-id="element-514" />
            </label>
            <label className="flex items-center justify-between" data-id="element-515">
              <span className="text-slate-700" data-id="element-516">Scheduling notifications</span>
              <input type="checkbox" defaultChecked className="w-5 h-5 text-orange-600 rounded" data-id="element-517" />
            </label>
            <label className="flex items-center justify-between" data-id="element-518">
              <span className="text-slate-700" data-id="element-519">Promotional emails</span>
              <input type="checkbox" className="w-5 h-5 text-orange-600 rounded" data-id="element-520" />
            </label>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <AnimatePresence mode="wait" data-id="element-521">
          {isEditing && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="flex gap-3" data-id="element-522">
              <motion.div whileTap={{
            scale: 0.98
          }} className="flex-1" data-id="element-523">
                <Button variant="primary" fullWidth onClick={handleSave} className="flex items-center justify-center gap-2" data-id="element-524">
                  <SaveIcon className="w-4 h-4" data-id="element-525" />
                  Save Changes
                </Button>
              </motion.div>
              <motion.div whileTap={{
            scale: 0.98
          }} className="flex-1" data-id="element-526">
                <Button variant="secondary" fullWidth onClick={handleCancel} data-id="element-527">
                  Cancel
                </Button>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}